import React, { useEffect, useRef, useState, useCallback } from "react";
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import CustomerDetails from "../../SalesForm/CustomerDetails";
import ProductForm from "./Components/ProductForm";
import ProductTable from "./Components/ProductTable";
import { useProductForm } from "./hooks/useProductCalculations";
import { useEstimateCalculations } from "./hooks/useEstimateCalculations";
import { DEFAULT_PRODUCT_FORM_DATA } from "./data/productFormSchema";
import baseURL from "../../../../../Url/NodeBaseURL";
import { red } from "@mui/material/colors";
import { BlobProvider } from "@react-pdf/renderer";
import EstimateA4 from "../EstimateA4";

// Import all sub-components for SalesFormSection
import EstimateOldSalesForm from "./Components/EstimateOldSalesForm";
import EstimateSchemesForm from "./Components/EstimateSchemesForm";
import EstimateSaleReturnForm from "./Components/EstimateSaleReturnForm";
import EstimateRepairsTable from "./Components/EstimateRepairsTable";
import EstimateOrdersTable from "./Components/EstimateOrdersTable";
import PaymentDetailsV2 from "./Components/PaymentDetailsV2";
import EstimateDetails from "../EstimateDetails";

const EstimateFormV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Remove URL parameter logic and replace with ID-based logic
  const [estimateId, setEstimateId] = useState(null); // Track the estimate ID for create/update logic
  
  // Customer form state
  const [customerFormData, setCustomerFormData] = useState({
    customer_id: "",
    customer_name: "",
    account_name: "",
    mobile: "",
    address: "",
    address1: "",
    address2: "",
    city: "",
    pincode: "",
    state: "",
    state_code: "",
    gst_no: "",
    gst_in: "",
    email: "",
    aadhar_card: "",
    pan_card: "",
  });
  
  // Estimate details state
  const [estimateFormData, setEstimateFormData] = useState({
    date: "",
    estimate_number: location.state?.estimateNumber || "",
  });

  // Estimate navigation state
  const [currentEstimateIndex, setCurrentEstimateIndex] = useState(0);
  const [totalEstimates, setTotalEstimates] = useState(1);
  const [allEstimates, setAllEstimates] = useState([]);
  const [sortedEstimates, setSortedEstimates] = useState([]);

  const [customers, setCustomers] = useState([]);
  const [selectedMobile, setSelectedMobile] = useState("");
  const mobileRef = useRef(null);

  // Image handling state
  const [showOptions, setShowOptions] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);

  // Festival offers state - declare before hook usage
  const [offers, setOffers] = useState([]);
  const [appliedOffers, setAppliedOffers] = useState([]);
  const [appliedOffersFromAPI, setAppliedOffersFromAPI] = useState([]);
  const [festivalDiscountAmt, setFestivalDiscountAmt] = useState(0);
  const [festivalShowModal, setFestivalShowModal] = useState(false);
  const [isAnyOfferApplied, setIsAnyOfferApplied] = useState(false);

  // Data sources for dropdowns - initially empty
  const [products, setProducts] = useState([]);
  const [data, setData] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [designOptions, setDesignOptions] = useState([]);
  const [purityOptions, setPurityOptions] = useState([]);
  const [metaltypeOptions, setMetaltypeOptions] = useState([]);
  const [allMetalTypes, setAllMetalTypes] = useState([]);
  const [allSubcategory, setAllSubcategory] = useState([]);

  // Header totals from backend (used to present exact saved numbers in edit mode)
  const [headerTotals, setHeaderTotals] = useState(null);
  const [shouldPrint, setShouldPrint] = useState(false);
  const [shouldResetAfterPrint, setShouldResetAfterPrint] = useState(false);

  // Use the product form hook for all product management
  const {
    productFormData,
    setProductFormData,
    isEditing,
    editingIndex,
    calculations,
    handleAdd: originalHandleAdd,
    handleUpdate: originalHandleUpdate,
    handleChange,
    clearForm,
    startEditing,
    cancelEditing,
    handleDeleteProduct: originalHandleDeleteProduct,
    getAllProducts,
    updateFormDataWithCalculations,
    handleTotalPriceChange,
    rates,
    fetchCurrentRates,
    autoCalculateRate,
    autoSelectDefaultPurity,
    applyFestivalDiscountToAllProducts,
    applyFestivalDiscountToProduct,
    loadProducts,
  } = useProductForm(DEFAULT_PRODUCT_FORM_DATA, offers, appliedOffersFromAPI);

  // Use the hook functions directly
  const handleAdd = () => {
    // When adding products, we should recalculate the net payable
    // but preserve the manual mode if user has edited it
    if (isManualNetMode && hasUserEditedNetPay && manualNetPayAmount > 0) {
      console.log('[EstimateFormV2] Adding product - will recalculate but preserve manual mode');
      // Add the product, then recalculate the manual amount
      return originalHandleAdd(() => {
        // After product is added, recalculate the manual net payable
        // This will be handled by the useEstimateCalculations hook
        console.log('[EstimateFormV2] Product added, manual mode preserved for recalculation');
        // Explicitly ensure manual mode is preserved
        setIsManualNetMode(true);
        setHasUserEditedNetPay(true);
      });
    } else {
      return originalHandleAdd(() => {
        // Reset manual mode and discount when adding new products
        resetManualMode();
        // Reset discount to 0 when a new product is added
        setDiscount(0);
        // Reset manual discount calculation flag when adding new products
        setHasManualDiscountCalculation(false);
        console.log('[EstimateFormV2] Product added, discount reset to 0');
      });
    }
  };

  const handleUpdate = () => {
    // Don't reset manual mode if we're already in it and have a user-edited amount
    if (isManualNetMode && hasUserEditedNetPay && manualNetPayAmount > 0) {
      console.log('[EstimateFormV2] Updating product while preserving manual mode');
      return originalHandleUpdate(() => {}); // Pass empty function to prevent reset
    } else {
      return originalHandleUpdate(() => {
        resetManualMode();
        // Reset manual discount calculation flag when updating products
        setHasManualDiscountCalculation(false);
      });
    }
  };

  const handleDeleteProduct = (index) => {
    // Don't reset manual mode if we're already in it and have a user-edited amount
    if (isManualNetMode && hasUserEditedNetPay && manualNetPayAmount > 0) {
      console.log('[EstimateFormV2] Deleting product while preserving manual mode');
      // The product deletion will trigger recalculation, but we want to preserve manual mode
    } else {
      // Reset manual discount calculation flag when deleting products (if not in manual mode)
      setHasManualDiscountCalculation(false);
    }
    originalHandleDeleteProduct(index);
  };



  // Sales data state - initially empty
  const [oldSalesData, setOldSalesData] = useState([]);
  const [oldTableData, setOldTableData] = useState([]);
  const [schemeSalesData, setSchemeSalesData] = useState([]);
  const [schemeTableData, setSchemeTableData] = useState([]);
  const [saleReturnData, setSaleReturnData] = useState([]);

  // Invoice and return data state - initially empty
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [uniqueInvoice, setUniqueInvoice] = useState([]);
  const [returnData, setReturnData] = useState([]);
  const [salesReturnData, setSalesReturnData] = useState([]);

  // Selection state
  const [selectedRows, setSelectedRows] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  // Order and repair state - initially empty
  const [orderData, setOrderData] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Calculation state
  const [salesTaxableAmount, setSalesTaxableAmount] = useState(0);
  const [salesTaxAmount, setSalesTaxAmount] = useState(0);
  const [salesNetAmount, setSalesNetAmount] = useState(0);

  // Form state
  const [saving, setSaving] = useState(false);
  const [resetActiveForm, setResetActiveForm] = useState(false);

  // State for manual total price change
    const [isManualTotalPriceChange, setIsManualTotalPriceChange] = useState(false);
    
    // State to track if we're loading an existing estimate (to prevent discount recalculation)
    const [isLoadingExistingEstimate, setIsLoadingExistingEstimate] = useState(false);

  // Sales form section state
  const [activeForm, setActiveForm] = useState("old");

  // Payment details state
  const [paymentDetails, setPaymentDetails] = useState({
    cash_amount: 0,
    card_amt: 0,
    chq_amt: 0,
    online_amt: 0,
  });



  // Manual amount handling
  const [manualNetAmount, setManualNetAmount] = useState(0);
  const [isManualNetMode, setIsManualNetMode] = useState(false);
  const [manualNetPayAmount, setManualNetPayAmount] = useState(0);
  const [hasUserEditedNetPay, setHasUserEditedNetPay] = useState(false);
  // Track user-initiated manual net changes
  const [manualChangeSeq, setManualChangeSeq] = useState(0);
  // Track last sales return count to detect removal of all
  const [lastSaleReturnCount, setLastSaleReturnCount] = useState(0);
  // Track if discount has been manually calculated to prevent overwriting
  const [hasManualDiscountCalculation, setHasManualDiscountCalculation] = useState(false);
  // Suppress one reverse recalculation when manual amount change is programmatic due to URD/SR
  const [suppressReverseOnce, setSuppressReverseOnce] = useState(false);
  // After a direct manual change, suppress the immediate sync-updater once
  const suppressNextManualSyncRef = useRef(false);

  // Discount state
  const [discount, setDiscount] = useState(0);

  // Function to reset manual mode when products are added
  const resetManualMode = useCallback(() => {
    // Only reset if user hasn't manually set a net payable amount
    if (manualNetPayAmount === 0) {
      setIsManualNetMode(false);
      setHasUserEditedNetPay(false);
      setManualNetPayAmount(0);
    }
  }, [manualNetPayAmount]);

  // Comprehensive function to reset all form data for new estimate
  const resetAllFormData = useCallback(() => {
    console.log('[EstimateFormV2] Resetting all form data for new estimate');
    
    // Reset estimate ID for new estimate
    setEstimateId(null);
    
    // Reset customer form data
    setCustomerFormData({
      customer_id: "",
      customer_name: "",
      account_name: "",
      mobile: "",
      address: "",
      address1: "",
      address2: "",
      city: "",
      pincode: "",
      state: "",
      state_code: "",
      gst_no: "",
      gst_in: "",
      email: "",
      aadhar_card: "",
      pan_card: "",
    });

    // Reset estimate form data
    setEstimateFormData({
      date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
      estimate_number: "",
    });

    // Reset estimate navigation state
    setCurrentEstimateIndex(0);
    setTotalEstimates(1);
    setAllEstimates([]);
    setSortedEstimates([]);

    // Reset customer selection
    setSelectedMobile("");

    // Reset image handling state
    setShowOptions(false);
    setShowWebcam(false);

    // Reset festival offers state
    setOffers([]);
    setAppliedOffers([]);
    setAppliedOffersFromAPI([]);
    setFestivalDiscountAmt(0);
    setFestivalShowModal(false);
    setIsAnyOfferApplied(false);

    // Reset dropdown data sources
    setCategoryOptions([]);
    setSubcategoryOptions([]);
    setDesignOptions([]);
    setPurityOptions([]);
    setMetaltypeOptions([]);
    setAllMetalTypes([]);

    // Reset header totals
    setHeaderTotals(null);
    setShouldPrint(false);
    setShouldResetAfterPrint(false);

    // Clear all products using the hook function
    loadProducts([]);

    // Reset sales data state
    setOldSalesData([]);
    setOldTableData([]);
    setSchemeSalesData([]);
    setSchemeTableData([]);
    setSaleReturnData([]);

    // Reset invoice and return data state
    setInvoiceDetails([]);
    setFilteredInvoices([]);
    setUniqueInvoice([]);
    setReturnData([]);
    setSalesReturnData([]);

    // Reset selection state
    setSelectedRows([]);
    setIsAllSelected(false);

    // Reset order and repair state
    setOrderData([]);
    setOrderDetails([]);
    setSelectedOrder([]);
    setRepairs([]);
    setShowModal(false);
    setLoading(false);

    // Reset calculation state
    setSalesTaxableAmount(0);
    setSalesTaxAmount(0);
    setSalesNetAmount(0);

    // Reset form state
    setSaving(false);
    setResetActiveForm(false);
    setIsManualTotalPriceChange(false);

    // Reset sales form section state
    setActiveForm("old");

    // Reset payment details state
    setPaymentDetails({
      cash_amount: 0,
      card_amt: 0,
      chq_amt: 0,
      online_amt: 0,
    });

    // Reset manual amount handling
    setManualNetAmount(0);
    setIsManualNetMode(false);
    setManualNetPayAmount(0);
    setHasUserEditedNetPay(false);
    setLastSaleReturnCount(0);
    setHasManualDiscountCalculation(false);

    // Reset discount state
    setDiscount(0);

    // Clear form using the hook function
    clearForm();

    console.log('[EstimateFormV2] All form data reset completed');
  }, [loadProducts, clearForm]);

  // v2 edit/new context - now using estimateFormData
  const estimate_number = estimateFormData.estimate_number;
  const isEditMode = !!location.state?.estimateNumber;

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${baseURL}/get/account-details`);
      if (!response.ok) {
        throw new Error("Failed to fetch customers");
      }
      const result = await response.json();
      const filteredCustomers = result.filter(
        (item) =>
          item.account_group === "CUSTOMERS" ||
          item.account_group === "SUPPLIERS"
      );
      setCustomers(filteredCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    try {
      const response = await fetch(`${baseURL}/get/products`);
      if (!response.ok) {
        throw new Error("Failed to fetch products");
      }
      const result = await response.json();
      setProducts(result);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  // Fetch all data
  const fetchData = async () => {
    try {
      const response = await fetch(`${baseURL}/get/opening-tags-entry`);
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const result = await response.json();
      setData(result.result || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Fetch categories
  const fetchCategory = async () => {
    try {
      const response = await fetch(`${baseURL}/get/products`);
      if (!response.ok) {
        throw new Error("Failed to fetch products");
      }
      const result = await response.json();
      if (result && Array.isArray(result)) {
        const formattedOptions = result.map((item) => ({
          label: item.product_name, // Display name
          value: item.product_name, // Unique value
        }));
        setCategoryOptions(formattedOptions);
      } else {
        console.error("Invalid API response format", result);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  // Fetch subcategories
  const fetchSubCategory = async () => {
    try {
      const response = await fetch(`${baseURL}/subcategory`);
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const result = await response.json();
      if (result && result.data) {
        // If productFormData.category is available, filter based on it, otherwise show all
        const filteredData = productFormData.category
          ? result.data.filter((item) => item.category === productFormData.category)
          : result.data;
        setAllSubcategory(result.data);

        // Format the filtered options
        const formattedOptions = filteredData.map((item) => ({
          label: item.sub_category_name, // Display value
          value: item.sub_category_name, // Unique ID for value
          selling_purity: item.selling_purity,
          printing_purity: item.printing_purity,
          purity: item.purity,
          pricing: item.pricing, // Include pricing field from subcategory
          pricingOpt:item.pricingOpt,
        }));

        setSubcategoryOptions(formattedOptions);
      } else {
        console.error("Invalid API response format", result);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Fetch metal types
  const fetchMetalType = async () => {
    try {
      const response = await fetch(`${baseURL}/metaltype`);
      const data = await response.json();

      // Extract all metal types
      const allMetalTypes = Array.from(
        new Set(data.map((product) => product.metal_name))
      );

      // Store all metal types
      setAllMetalTypes(allMetalTypes);

      // Initially, show all metal types
      setMetaltypeOptions(
        allMetalTypes.map((category) => ({
          value: category,
          label: category,
        }))
      );
    } catch (error) {
      console.error("Error fetching metal types:", error);
    }
  };

  // Fetch design options
  const fetchDesignOptions = async () => {
    try {
      const response = await fetch(`${baseURL}/designmaster`);
      if (!response.ok) {
        throw new Error("Failed to fetch designs");
      }
      const designData = await response.json();
      const options = designData.map((design) => ({
        value: design.design_name,
        label: design.design_name,
      }));
      setDesignOptions(options);
    } catch (error) {
      console.error("Error fetching design options:", error);
    }
  };

  // Fetch purity options
  const fetchPurityOptions = async () => {
    try {
      const response = await fetch(`${baseURL}/purity`);
      if (!response.ok) {
        throw new Error("Failed to fetch purity options");
      }
      const purityData = await response.json();
      
      const options = purityData.map((purity) => {
        const value = purity.purity_name || purity.name || purity.purity || purity.value || "";
        const label = purity.purity_name || purity.name || purity.purity || purity.label || value;
        
        return {
          value: value,
          label: label,
        };
      }).filter(option => option.value && option.label);
      
      setPurityOptions(options);
    } catch (error) {
      console.error("Error fetching purity options:", error);
    }
  };

  // Handle barcode change
  const handleBarcodeChange = async (code) => {
    try {
      if (!code) {
        setProductFormData((prevData) => ({
          ...prevData,
          code: "",
          product_id: "",
          product_name: "",
          metal_type: "",
          design_name: "",
          purity: "",
          selling_purity: "",
          printing_purity: "",
          category: "",
          sub_category: "",
          gross_weight: "",
          stone_weight: "",
          stone_price: "",
          weight_bw: "",
          va_on: "Gross Weight",
          va_percent: "",
          wastage_weight: "",
          total_weight_av: "",
          pieace_cost: "",
          mrp_price: "",
          mc_on: "MC %",
          mc_per_gram: "",
          making_charges: "",
          rate: "",
          pieace_cost: "",
          mrp_price: "",
          rate_amt: "",
          tax_percent: "03% GST",
          tax_amt: "",
          hm_charges: "60.00",
          total_price: "",
          qty: "",
          remarks: "",
          piece_taxable_amt: "",
          festival_discount: "",
          custom_purity: "",
          opentag_id: "", // Clear opentag_id when barcode is cleared
        }));
        return;
      }

      const product = products.find(
        (prod) => String(prod.rbarcode) === String(code)
      );
      
      // Also check in data for tag-based barcodes
      const tag = data?.find(
        (tag) => String(tag.PCode_BarCode) === String(code)
      );
      
      if (product) {
        const metalType = product.Category || "";
        const mcOnValue =
          metalType.toLowerCase() === "silver" ? "MC / Gram" : "MC %";
        
        setProductFormData((prevData) => ({
          ...prevData,
          code: code,
          product_id: product.product_id,
          product_name: "",
          metal_type: product.Category,
          design_name: "",
          category: product.product_name,
          sub_category: "",
          gross_weight: "",
          stone_weight: "",
          stone_price: "",
          weight_bw: "",
          va_on: "Gross Weight",
          va_percent: "",
          wastage_weight: "",
          total_weight_av: "",
          pieace_cost: "",
          mrp_price: "",
          mc_on: mcOnValue,
          mc_per_gram: "",
          making_charges: "",
          rate: "",
          pieace_cost: "",
          mrp_price: "",
          rate_amt: "",
          tax_percent: "03% GST",
          tax_amt: "",
          hm_charges: "60.00",
          total_price: "",
          qty: "",
          remarks: "",
          piece_taxable_amt: "",
          festival_discount: "",
          custom_purity: "",
          purity: "", // Clear purity when barcode changes
          selling_purity: "", // Clear selling_purity when barcode changes
          opentag_id: "", // Clear opentag_id when switching to normal product
        }));
      } else if (tag) {
        // If the tag is marked as "Sold"
        if (
          tag.Status === "Sold" ||
          tag.Status === "Sale Returned" ||
          tag.Status === "Reserved"
        ) {
          alert("The product is already sold out!");
          return;
        }

        const productId = tag.product_id;
        const productDetails = products.find(
          (prod) => String(prod.product_id) === String(productId)
        );

        const tagPricing = tag.Pricing || productFormData.pricing || "By Weight";
        const parsePercent = (val) => {
          if (val === null || val === undefined || val === "") return 0;
          const s = val.toString().replace("%", "").trim();
          const n = Number(s);
          return Number.isFinite(n) ? n : 0;
        };

        // Fetch purchase record using tag's invoice number to get actual purchase values
        let purchaseRecord = null;
        if (tag.invoice) {
          try {
            const purchaseResponse = await axios.get(`${baseURL}/get-purchase-details/${tag.invoice}`);
            const purchaseData = purchaseResponse.data?.repeatedData || [];
            
            // Find the specific product in the purchase record that matches this tag
            purchaseRecord = purchaseData.find(
              (item) => String(item.product_id) === String(tag.product_id)
            );
          } catch (error) {
            console.error("Error fetching purchase record:", error);
          }
        }

        // Fetch subcategory details to get selling addon values
        let subcategoryData = null;
        try {
          const subcategoryResponse = await axios.get(`${baseURL}/get/subcategories`);
          subcategoryData = subcategoryResponse.data.find(
            (sub) => sub.sub_category_name === tag.sub_category
          );
        } catch (error) {
          console.error("Error fetching subcategory details:", error);
        }

        // For "By Running Tag", calculate combined values (Purchase + Selling Addon)
        let combinedPurity = "";
        let combinedWastage = "";
        let combinedMC = "";
        
        if (tagPricing === "By Running Tag") {
          // Get purchase values from purchase record (fetched by invoice), not tag
          let purchasePurity = 0;
          if (purchaseRecord) {
            // Use purchase record values
            if (purchaseRecord.Purity && String(purchaseRecord.Purity).toLowerCase().includes("manual")) {
              purchasePurity = parsePercent(purchaseRecord.purityPercentage || "");
            } else {
              purchasePurity = parsePercent(purchaseRecord.Purity || purchaseRecord.purityPercentage || "");
            }
          } else {
            // Fallback to tag values if purchase record not found
            if (tag.pur_Purity && String(tag.pur_Purity).toLowerCase().includes("manual")) {
              purchasePurity = parsePercent(tag.pur_purityPercentage || tag.purityPercentage || "");
            } else {
              purchasePurity = parsePercent(tag.pur_Purity || tag.pur_purityPercentage || tag.purityPercentage || "");
            }
          }
          
          const purchaseWastage = purchaseRecord 
            ? parsePercent(purchaseRecord.wastage || "")
            : parsePercent(tag.pur_Wastage_Percentage || "");
          
          // Get purchase MC - only if MC On is "MC %"
          let purchaseMC = 0;
          const mcOnValue = purchaseRecord?.Making_Charges_On || tag.pur_Making_Charges_On || "";
          if (mcOnValue === "MC %") {
            purchaseMC = purchaseRecord
              ? parsePercent(purchaseRecord.Making_Charges_Value || "")
              : parsePercent(tag.pur_MC_Per_Gram || "");
          }
          
          // Get selling addon values from subcategory
          const sellingAddonPurity = parsePercent(
            subcategoryData?.selling_addon_purity || 
            tag.selling_addon_purity || 
            ""
          );
          const sellingAddonWastage = parsePercent(
            subcategoryData?.selling_addon_wastage_percent || 
            tag.selling_addon_wastage_percent || 
            ""
          );
          const sellingAddonMC = parsePercent(
            subcategoryData?.selling_addon_mc_percent || 
            tag.selling_addon_mc_percent || 
            ""
          );
          
          // Calculate combined values
          // Selling Purity = Purchase Purity + Selling Addon Purity only (MC and Wastage shown separately)
          const totalPurity = purchasePurity + sellingAddonPurity;
          const totalWastage = purchaseWastage + sellingAddonWastage;
          const totalMC = purchaseMC + sellingAddonMC;
          
          // Format for display
          combinedPurity = totalPurity > 0 ? totalPurity.toFixed(2) : "";
          combinedWastage = totalWastage > 0 ? totalWastage.toFixed(2) : "";
          combinedMC = totalMC > 0 ? totalMC.toFixed(2) : "";
        }

        // Get purchase values for formData storage
        let purchasePurityValue = "";
        let purchaseWastageValue = "";
        let purchaseMCValue = "";
        let purchaseMcOnValue = "";
        
        if (tagPricing === "By Running Tag") {
          // Get purchase purity from purchase record, then tag
          if (purchaseRecord?.Purity && String(purchaseRecord.Purity).toLowerCase().includes("manual")) {
            purchasePurityValue = purchaseRecord.purityPercentage || "";
          } else if (purchaseRecord?.Purity) {
            purchasePurityValue = purchaseRecord.Purity;
          } else if (purchaseRecord?.purityPercentage) {
            purchasePurityValue = purchaseRecord.purityPercentage;
          } else if (tag.pur_Purity && String(tag.pur_Purity).toLowerCase().includes("manual")) {
            purchasePurityValue = tag.pur_purityPercentage || tag.purityPercentage || "";
          } else {
            purchasePurityValue = tag.pur_Purity || tag.pur_purityPercentage || tag.purityPercentage || "";
          }

          // Get purchase wastage from purchase record, then tag
          purchaseWastageValue = purchaseRecord?.wastage || tag.pur_Wastage_Percentage || "";

          // Get purchase MC from purchase record, then tag
          purchaseMcOnValue = purchaseRecord?.Making_Charges_On || tag.pur_Making_Charges_On || "";
          if (purchaseMcOnValue === "MC %") {
            purchaseMCValue = purchaseRecord?.Making_Charges_Value || tag.pur_MC_Per_Gram || "";
          }
        }

        // Calculate the rate based on metal type
        let rateValue = "";
        const metalType = String(tag.metal_type || "").toLowerCase();
        
        // For "By Running Tag", use combined purity for rate calculation
        // Otherwise, use the selling purity from tag
        let purityForCalculation = null;
        if (tagPricing === "By Running Tag" && combinedPurity) {
          // Use combined purity (Purchase + Selling Addon) for rate calculation
          purityForCalculation = parseFloat(combinedPurity);
        } else {
          // Use selling purity for non-running tag items
        const purity = tag.Purity || tag.pur_Purity || "";
          purityForCalculation = parseFloat(purity);
        }

        if (purityForCalculation && !isNaN(purityForCalculation)) {
          const baseRate = metalType === "silver" ? rates.silver_rate : rates.rate_24crt;
          if (baseRate) {
            rateValue = (purityForCalculation * parseFloat(baseRate)) / 100;
          }
        } else if (metalType === "silver") {
          rateValue = rates.silver_rate || "";
        } else {
        rateValue = tag.rate || "";
        }

        setProductFormData((prevData) => ({
          ...prevData,
          code: tag.PCode_BarCode || "",
          product_id: tag.product_id || "",
          opentag_id: tag.opentag_id || "",
          product_name: tag.sub_category || "",
          metal_type: tag.metal_type || "",
          design_name: tag.design_master || "",
          // For "By Running Tag", use combined purity (Purchase + Selling Addon), otherwise use tag value
          purity: tagPricing === "By Running Tag" && combinedPurity 
            ? combinedPurity 
            : (tag.pur_Purity || ""),
          selling_purity: tagPricing === "By Running Tag" && combinedPurity 
            ? combinedPurity 
            : (tag.Purity || ""),
          printing_purity: tag.printing_purity || "",
          pricing: tagPricing,
          category: tag.category || "",
          sub_category: tag.sub_category || "",
          gross_weight: tag.Gross_Weight || "",
          stone_weight: tag.Stones_Weight || "",
          stone_price: tag.Stones_Price || "",
          weight_bw: tag.Weight_BW || "",
          va_on: tag.Wastage_On || "Gross Weight",
          // For "By Running Tag", use combined wastage (Purchase + Selling Addon), otherwise use tag value
          va_percent: tagPricing === "By Running Tag" && combinedWastage 
            ? combinedWastage 
            : (tag.Wastage_Percentage || ""),
          wastage_weight: tag.WastageWeight || "",
          total_weight_aw: tag.TotalWeight_AW || "",
          pieace_cost: tag.pieace_cost || "",
          mrp_price: tag.mrp_price || "",
          // For "By Running Tag", use purchase record's MC On and combined MC (Purchase + Selling Addon)
          mc_on: tagPricing === "By Running Tag" 
            ? (purchaseMcOnValue || "MC %")
            : (tag.Making_Charges_On || "MC %"),
          // For "By Running Tag", MC % should be the combined value (Purchase MC + Selling Addon MC)
          mc_per_gram: tagPricing === "By Running Tag" && combinedMC 
            ? combinedMC 
            : (tag.MC_Per_Gram || ""),
          making_charges: tag.Making_Charges || "",
          tax_percent: productDetails?.tax_slab || tag.tax_percent || "03% GST",
          qty: 1,
          rate: rateValue,
          hm_charges: "60.00",
          total_price: "",
          remarks: "",
          piece_taxable_amt: "",
          festival_discount: "",
          custom_purity: "",
          // Store purchase values in formData for calculateSellingPurityForRunningTag
          pur_Purity: purchasePurityValue,
          pur_purityPercentage: purchasePurityValue,
          pur_Wastage_Percentage: purchaseWastageValue,
          pur_MC_Per_Gram: purchaseMCValue,
          pur_Making_Charges_On: purchaseMcOnValue,
          // Store selling addon values in formData
          selling_addon_purity: subcategoryData?.selling_addon_purity || tag.selling_addon_purity || "",
          selling_addon_mc_percent: subcategoryData?.selling_addon_mc_percent || tag.selling_addon_mc_percent || "",
          selling_addon_wastage_percent: subcategoryData?.selling_addon_wastage_percent || tag.selling_addon_wastage_percent || "",
        }));
      }
    } catch (error) {
      console.error("Error in handleBarcodeChange:", error);
    }
  };

  // Handle product name change
  const handleProductNameChange = (productName) => {
    const selectedSubcategory = subcategoryOptions.find(
      (option) => option.value === productName
    );

    if (selectedSubcategory) {
      const subcategoryPurity = selectedSubcategory.selling_purity || selectedSubcategory.purity || "";
      const subcategoryPrintingPurity = selectedSubcategory.printing_purity || "";
      
      setProductFormData((prevData) => ({
        ...prevData,
        product_name: productName,
        purity: subcategoryPurity,
        selling_purity: subcategoryPurity, // Set selling_purity from subcategory
        design_name: "",
        printing_purity: subcategoryPrintingPurity, // Set printing_purity from subcategory
        pricing: selectedSubcategory.pricing || "By Weight", // Set pricing based on subcategory
        pricingOpt: selectedSubcategory.pricingOpt || ""
      }));
    } else {
      setProductFormData((prevData) => ({
        ...prevData,
        product_name: productName,
        design_name: "",
        selling_purity: "",
        printing_purity: "",
        pricing: "By Weight", // Reset to default when no subcategory is selected
      }));
    }
  };

  // Handle metal type change
  const handleMetalTypeChange = (metalType) => {
    setProductFormData((prevData) => ({
      ...prevData,
      metal_type: metalType,
    }));
  };

  // Handle design name change
  const handleDesignNameChange = (designName) => {
    setProductFormData((prevData) => ({
      ...prevData,
      design_name: designName,
    }));
  };

  // Handle image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setProductFormData((prevData) => ({
          ...prevData,
          product_image: file,
          imagePreview: event.target.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear image
  const clearImage = () => {
    setProductFormData((prevData) => ({
      ...prevData,
      product_image: null,
      imagePreview: null,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Capture image from webcam
  const captureImage = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setProductFormData((prevData) => ({
        ...prevData,
        imagePreview: imageSrc,
      }));
      setShowWebcam(false);
    }
  };

  // Handle add customer
  const handleAddCustomer = (mobile) => {
    // Navigate to customer add page with mobile number
    navigate("/add-customer", { state: { mobile } });
  };

  // Handle customer change
  const handleCustomerChange = (customerId) => {
    const selectedCustomer = customers.find(
      (customer) => customer.account_id === customerId
    );
    
    if (selectedCustomer) {
      setCustomerFormData({
        customer_id: selectedCustomer.account_id,
        customer_name: selectedCustomer.customer_name,
        account_name: selectedCustomer.account_name,
        mobile: selectedCustomer.mobile,
        address: selectedCustomer.address,
        address1: selectedCustomer.address1,
        address2: selectedCustomer.address2,
        city: selectedCustomer.city,
        pincode: selectedCustomer.pincode,
        state: selectedCustomer.state,
        state_code: selectedCustomer.state_code,
        gst_no: selectedCustomer.gst_no,
        gst_in: selectedCustomer.gst_in,
        email: selectedCustomer.email,
        aadhar_card: selectedCustomer.aadhar_card,
        pan_card: selectedCustomer.pan_card,
      });
      setSelectedMobile(selectedCustomer.mobile);
    }
  };

  // Additional handlers for estimate functionality
  const handleInvoiceChange = (e) => {
    // Implementation for invoice change
  };

  const handleCheckboxChange = (event, index) => {
    // Implementation for checkbox change
  };

  const handleSelectAllChange = (event) => {
    // Implementation for select all change
  };

  const handleCheckout = async () => {
    // Implementation for checkout
  };

  const resetSaleReturnForm = () => {
    // Implementation for reset sale return form
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleViewDetails = async (order_number) => {
    // Implementation for view details
  };

  const handleOrderCheckboxChange = async (e, order_number) => {
    // Implementation for order checkbox change
  };

  const handleRepairCheckboxChange = async (e, repair_no) => {
    // Implementation for repair checkbox change
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  // Payment and calculation handlers
  const handleSave = async (skipPrintAndReset = false) => {
    if (!allProducts || allProducts.length === 0) {
      alert("Please add at least one product before saving");
      return;
    }

    setSaving(true);
    try {
      const productsToSave = allProducts;
      const dateStr = estimateFormData.date || new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

      // 1) Upsert estimate header
      const estimatePayload = {
        estimate_number,
        round_off: +(Math.round(netAmount) - netAmount).toFixed(2),
        online_amt: Number(paymentDetails.online_amt || 0),
        card_amt: Number(paymentDetails.card_amt || 0),
        cheque_amt: Number(paymentDetails.chq_amt || 0),
        cash_amt: Number(paymentDetails.cash_amount || 0),
        date: dateStr,
        total_amt: Number(totalAmount || 0),
        discount_amt: Number(discountAmt || 0),
        discount_percent: Number(discount || 0), // Save discount percentage
        fest_discount_amt: Number(festivalDiscountAmt || 0),
        taxable_amt: Number(taxableAmount || 0),
        tax_amt: Number(taxAmount || 0),
        net_amt: Number(netAmount || 0),
        old_items_amt: Number(oldItemsAmount || 0),
        scheme_amt: Number(schemeAmount || 0),
        sale_return_amt: Number(salesAmountToPass || 0),
        net_payable_amt: Number(netPayableAmount || 0),
      };

      if (customerFormData.customer_id) {
        estimatePayload.account_id = customerFormData.customer_id;
      }

      let currentEstimateId;
      
      // ID-based logic: Check if we have an existing estimate ID
      if (estimateId) {
        // We have an existing estimate ID, so this is an update
        console.log('[EstimateFormV2] Updating existing estimate with ID:', estimateId);
        await axios.put(`${baseURL}/Estimate/update/${estimate_number}`, estimatePayload);
        currentEstimateId = estimateId;
      } else {
        // No existing ID, so this is a create operation
        console.log('[EstimateFormV2] Creating new estimate');
        
        // Check the latest estimate number right before saving to ensure we get the correct next number
        try {
          const latestResponse = await axios.get(`${baseURL}/Estimate/summary`);
          const latestEstimates = latestResponse.data.summary || [];
          
          // Extract all estimate numbers and find the highest
          const latestEstimateNumbers = latestEstimates
            .map(est => est.uniqueData?.estimate_number)
            .filter(num => num && num.startsWith('EST-'))
            .map(num => {
              const match = num.match(/EST-(\d+)/);
              return match ? parseInt(match[1]) : 0;
            })
            .filter(num => !isNaN(num) && num > 0);
          
          // Find the next sequential number
          let nextSequentialNumber = 1;
          if (latestEstimateNumbers.length > 0) {
            const highestNumber = Math.max(...latestEstimateNumbers);
            if (highestNumber > 0) {
              nextSequentialNumber = highestNumber + 1;
            }
          }
          
          // Update the estimate payload with the correct sequential number
          const correctEstimateNumber = `EST-${nextSequentialNumber}`;
          estimatePayload.estimate_number = correctEstimateNumber;
          
          console.log('[EstimateFormV2] Updated estimate number before save:', {
            originalNumber: estimateFormData.estimate_number,
            newNumber: correctEstimateNumber,
            highestFromDB: latestEstimateNumbers.length > 0 ? Math.max(...latestEstimateNumbers) : 0
          });
          
          // Update the form state with the correct number
          setEstimateFormData(prev => ({
            ...prev,
            estimate_number: correctEstimateNumber
          }));
          
        } catch (error) {
          console.error('Error fetching latest estimate numbers:', error);
          // Continue with the original estimate number if fetch fails
        }
        
        const created = await axios.post(`${baseURL}/Estimate/create`, estimatePayload);
        currentEstimateId = created.data.estimate_id;
        
        // Store the new estimate ID for future updates
        setEstimateId(currentEstimateId);
        
        // If the backend returns a new estimate number, update the form
        if (created.data.estimate_number) {
          setEstimateFormData(prev => ({
            ...prev,
            estimate_number: created.data.estimate_number
          }));
        }
      }

      // 2) Save or update products (v1 approach - update existing, create new ones)
      // Use the actual product discounts that are already calculated and stored in the UI
      for (const item of productsToSave) {
        const numericTaxPercMatch = String(item.tax_percent || '').match(/\d+(?:\.\d+)?/);
        const taxPercNumber = numericTaxPercMatch ? parseFloat(numericTaxPercMatch[0]) : 3;

        // Use the actual discount that was calculated and stored in the product object
        const productDiscount = parseFloat(item.disscount || 0);

        // Create FormData for file upload support
        const productPayload = {
          barcode: item.code || '',
          estimate_number: estimateFormData.estimate_number, // Use the current estimate number (may have been updated)
          product_name: item.product_name || '',
          metal: item.metal_type || '',
          purity: item.purity || '',
          printing_purity: item.printing_purity || '',
          gr_wt: item.gross_weight || 0,
          st_wt: item.stone_weight || 0,
          stone_price: item.stone_price || 0,
          va_perc: item.va_percent || 0,
          total_wt: item.total_weight_av || 0,
          rate: item.rate || 0,
          mc: item.making_charges || 0,
          mc_on: item.mc_on || 'MC %',
          mc_per_gram: item.mc_per_gram || 0,
          discount: parseFloat(productDiscount).toFixed(2),
          fest_discount: item.festival_discount || 0,
          tax_perc: taxPercNumber,
          tax_amt: item.tax_amt || 0,
          total_price: item.total_price || 0,
          status: item.transaction_status || 'Sales',
          category: item.category || '',
          sub_category: item.sub_category || item.product_name || '',
          design_name: item.design_name || '',
          product_status: item.estimate_status || 'Not Delivered',
          estimate_id: currentEstimateId,
          remark_type: item.remarks_type || '',
          remark_value: item.remarks || '',
          image: item.imagePreview || '', // Send base64 string as before
          qty: item.qty || 0,
          piece_cost: item.pieace_cost || 0,
          pricing: item.pricing || '', 
          mrp_price: item.mrp_price || 0,
          piece_taxable_amt: item.piece_taxable_amt || 0,
          opentag_id: item.opentag_id || null,
        };



        if (item.id) {
          // Product has an ID, try to update it
          try {
            await axios.put(`${baseURL}/Product/update/${item.id}`, productPayload);
          } catch (error) {
            console.warn(`Failed to update product ${item.id}:`, error);
            // If update fails, try to create new one
            await axios.post(`${baseURL}/Product/create-product`, productPayload);
          }
        } else {
          // Product doesn't have an ID, create new one
          await axios.post(`${baseURL}/Product/create-product`, productPayload);
        }
      }



      // 3) Replace existing URD items for this estimate, then save new ones
      try {
        const existingOld = await axios.get(`${baseURL}/get/olditems/${estimateFormData.estimate_number}`);
        if (existingOld.data && existingOld.data.length > 0) {
          for (const existingItem of existingOld.data) {
            if (existingItem.id) {
              await axios.delete(`${baseURL}/delete/olditems/${existingItem.id}`);
            }
          }
        }
      } catch (e) {
        // continue
      }

      if (oldSalesData && oldSalesData.length > 0) {
        for (const item of oldSalesData) {
          const urdPayload = {
            estimate_number: estimateFormData.estimate_number, // Use the current estimate number (may have been updated)
            estimate_id: currentEstimateId, // Use the current estimate ID (newly created or existing)
            product_name: item.product,
            metal: item.metal,
            purity: item.purity,
            purity_percentage: item.purityPercentage || '',
            hsn_code: item.hsn_code || '',
            gross: parseFloat(item.gross) || 0,
            dust: parseFloat(item.dust) || 0,
            ml_percent: parseFloat(item.ml_percent) || 0,
            net_wt: parseFloat(item.net_wt) || 0,
            remarks: item.remarks || '',
            rate: parseFloat(item.rate) || 0,
            total_amount: parseFloat(item.total_amount) || 0,
            status: 'URD Purchase',
            transaction_type: 'estimate_urd_purchase',
          };
          await axios.post(`${baseURL}/save-urd-purchase-item`, urdPayload);
        }
      }

      // 4) Save sales return items (from v2 saleReturnData list)
      if (saleReturnData && saleReturnData.length > 0) {
        for (const ret of saleReturnData) {
          const numericTaxPerc = parseFloat(ret.tax_percent) || 0;
          const salesReturnPayload = {
            estimate_number: estimateFormData.estimate_number, // Use the current estimate number (may have been updated)
            estimate_id: currentEstimateId, // Use the current estimate ID (newly created or existing)
            original_invoice_number: ret.original_invoice_number || ret.invoice_number || '',
            product_name: ret.product_name || '',
            metal: ret.metal_type || '',
            purity: ret.purity || '',
            gross_weight: parseFloat(ret.gross_weight) || 0,
            stone_weight: parseFloat(ret.stone_weight) || 0,
            rate: parseFloat(ret.rate) || 0,
            rate_amt: parseFloat(ret.rate_amt) || 0,
            making_charges: parseFloat(ret.making_charges) || 0,
            stone_price: parseFloat(ret.stone_price) || 0,
            hm_charges: parseFloat(ret.hm_charges) || 0,
            disscount: parseFloat(ret.disscount) || 0,
            festival_discount: parseFloat(ret.festival_discount) || 0,
            tax_percent: numericTaxPerc,
            tax_amt: parseFloat(ret.tax_amt) || 0,
            total_price: parseFloat(ret.total_price) || 0,
            qty: parseFloat(ret.qty) || 1,
            status: 'Estimate Sale Returned',
            transaction_type: 'estimate_sale_return',
            return_date: ret.return_date || dateStr,
            remarks: ret.remarks || '',
            category: ret.category || '',
            sub_category: ret.sub_category || '',
            design_name: ret.design_name || '',
            code: ret.code || '',
            customer_id: customerFormData.customer_id || '',
            customer_name: customerFormData.account_name || '',
          };
          await axios.post(`${baseURL}/save-estimate-sale-return-item`, salesReturnPayload);
        }
      }

      if (!skipPrintAndReset) {
        const message = !estimateId ? 'New estimate created successfully' : 'Estimate saved successfully';
        alert(message);
        
        // Trigger PDF print after save without blocking the UI/loading state
        setShouldPrint(true);
        setShouldResetAfterPrint(true);
      }
    } catch (error) {
      console.error('Error saving estimate:', error);
      alert('Failed to save estimate. See console for details.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Function to apply discount to individual products
  const applyDiscountToProducts = (discountPercent) => {
    console.log('[EstimateFormV2] Applying discount to products:', discountPercent);
    
    const currentProducts = getAllProducts();
    if (!currentProducts || currentProducts.length === 0) {
      console.log('[EstimateFormV2] No products to apply discount to');
      return;
    }

    // Calculate total taxable base for discount distribution using the same logic as useEstimateCalculations
    // Use original_piece_taxable_amt if available (before discount), otherwise use piece_taxable_amt
    const totalTaxableBase = currentProducts.reduce((sum, product) => {
      // Prefer original_piece_taxable_amt if available (before discount)
      const originalPieceTaxable = parseFloat(product.original_piece_taxable_amt) || 0;
      const currentPieceTaxable = parseFloat(product.piece_taxable_amt) || 0;
      
      if (originalPieceTaxable > 0) {
        return sum + originalPieceTaxable;
      } else if (currentPieceTaxable > 0) {
        return sum + currentPieceTaxable;
      } else {
        // Fallback: derive from total_price by removing tax (3% GST)
        const totalPrice = parseFloat(product.total_price) || 0;
        if (totalPrice > 0) {
          const derivedTaxable = totalPrice / (1 + (3 / 100)); // 3% GST
          return sum + derivedTaxable;
        }
      }
      return sum;
    }, 0);

    console.log('[EstimateFormV2] Total taxable base for discount distribution:', totalTaxableBase);

    // Calculate discount amount using the same logic as the estimate calculation
    const discountAmount = (totalTaxableBase * parseFloat(discountPercent || 0)) / 100;

    // Update products with distributed discount
    const productsWithUpdatedDiscount = currentProducts.map(product => {
      // Use the same taxable base calculation as above for consistency
      const originalPieceTaxable = parseFloat(product.original_piece_taxable_amt) || 0;
      const currentPieceTaxable = parseFloat(product.piece_taxable_amt) || 0;
      
      let productTaxableBase = 0;
      if (originalPieceTaxable > 0) {
        productTaxableBase = originalPieceTaxable;
      } else if (currentPieceTaxable > 0) {
        productTaxableBase = currentPieceTaxable;
      } else {
        // Fallback: derive from total_price
        const totalPrice = parseFloat(product.total_price) || 0;
        if (totalPrice > 0) {
          productTaxableBase = totalPrice / (1 + (3 / 100)); // 3% GST
        }
      }
      
      let productDiscount = 0;
      if (productTaxableBase > 0 && totalTaxableBase > 0) {
        productDiscount = (productTaxableBase / totalTaxableBase) * discountAmount;
      }

      // Store original taxable amount before discount (if not already stored)
      const originalTaxable = product.original_piece_taxable_amt || productTaxableBase;
      
      // Recalculate product totals with new discount
      const updatedPieceTaxable = productTaxableBase - productDiscount;
      const taxAmount = (updatedPieceTaxable * 3) / 100; // 3% GST
      const updatedTotalPrice = updatedPieceTaxable + taxAmount;

      return {
        ...product,
        original_piece_taxable_amt: parseFloat(originalTaxable).toFixed(2),
        disscount: parseFloat(productDiscount).toFixed(2),
        piece_taxable_amt: parseFloat(updatedPieceTaxable).toFixed(2),
        tax_amt: parseFloat(taxAmount).toFixed(2),
        total_price: parseFloat(updatedTotalPrice).toFixed(2),
      };
    });

    console.log('[EstimateFormV2] Updated products with discount:', productsWithUpdatedDiscount);
    
    // Load the updated products back into the state
    loadProducts(productsWithUpdatedDiscount);
    
    // Mark that discount has been manually calculated to prevent overwriting
    setHasManualDiscountCalculation(true);
    
    // Ensure the discount percentage is set so that discountAmt gets calculated correctly
    // This ensures the payment details section shows the correct discount amount
    if (Math.abs(discountPercent - discount) > 0.01) {
      console.log('[EstimateFormV2] Updating discount percentage to ensure payment details reflect changes');
      setDiscount(discountPercent);
    }
  };

  const handleDiscountChange = (e) => {
    const newDiscount = parseFloat(e.target.value) || 0;
    setDiscount(newDiscount);
    
    // Apply the discount to products immediately
    applyDiscountToProducts(newDiscount);
  };

  const handleFestivalShowModal = () => {
    setFestivalShowModal(true);
  };

  const handleFestivalCloseModal = () => {
    setFestivalShowModal(false);
  };

  // Fetch festival offers
  const fetchFestivalOffers = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/offers`);
      const allOffers = response.data;

      const today = new Date();

      // Filter offers that are currently valid (within date range) AND not unapplied
      const validOffers = allOffers.filter((offer) => {
        const validFrom = new Date(offer.valid_from);
        const validTo = new Date(offer.valid_to);
        const isDateValid = today >= validFrom && today <= validTo;
        const isNotUnapplied = !offer.offer_status || offer.offer_status.toLowerCase() !== "unapplied";
        return isDateValid && isNotUnapplied;
      });

      // Separate applied and available offers (excluding unapplied ones)
      const appliedOffersAPI = validOffers.filter(offer => 
        offer.offer_status && offer.offer_status.toLowerCase() === "applied"
      );
      const availableOffers = validOffers.filter(offer => 
        !offer.offer_status || offer.offer_status.toLowerCase() === "available" || offer.offer_status === ""
      );

      setOffers(availableOffers);
      setAppliedOffersFromAPI(appliedOffersAPI);
    } catch (error) {
      console.error("Error fetching offers:", error);
    }
  };

  const handleApply = (selectedOffer, offerIndex) => {
    // If no items to apply offer to, just mark the offer as applied
    if (allProducts.length === 0) {
      setAppliedOffers(prev => [...prev, selectedOffer]);
      setIsAnyOfferApplied(true);
      return;
    }

    // Apply festival discount to all products using the hook function
    applyFestivalDiscountToAllProducts(selectedOffer);

    // Mark the offer as applied
    setAppliedOffers(prev => [...prev, selectedOffer]);
    setIsAnyOfferApplied(true);
  };

  // Removed unused handleManualNetAmountChange to satisfy linter

  const handleManualNetPayAmountChange = (value) => {
    console.log('[EstimateFormV2] handleManualNetPayAmountChange called:', {
      inputValue: value,
      currentState: {
        manualNetPayAmount,
        hasUserEditedNetPay,
        isManualNetMode,
        totalAmount,
        oldItemsAmount,
        salesAmountToPass
      }
    });

    // 1) Persist the manual net payable amount and enable manual mode
    setManualNetPayAmount(value);
    setHasUserEditedNetPay(true);
    setIsManualNetMode(true);
    setManualChangeSeq((prev) => prev + 1);
    // Suppress the next manual amount sync to prevent snapping back once
    suppressNextManualSyncRef.current = true;

    console.log('[EstimateFormV2] State updated, calculating discount...');

    // 2) Calculate the discount percentage via reverse calculation so that:
    // targetNetAmount = manualNetPay + URD + SR
    // targetTaxable = targetNetAmount / (1 + taxRate)
    // requiredDiscountAmt = totalAmount - targetTaxable
    try {
      const taxRate = 3; // 3% GST
      const targetNetAmount = Number(value || 0) + Number(oldItemsAmount || 0) + Number(salesAmountToPass || 0);
      const targetTaxable = targetNetAmount / (1 + taxRate / 100);
      const requiredDiscountAmt = Math.max(0, Number(totalAmount || 0) - targetTaxable - Number(festivalDiscountAmt || 0));
      const requiredDiscountPercent = Number(totalAmount) > 0 ? (requiredDiscountAmt / Number(totalAmount)) * 100 : 0;

      console.log('[EstimateFormV2] Discount calculation:', {
        targetNetAmount,
        targetTaxable,
        requiredDiscountAmt,
        requiredDiscountPercent,
        currentDiscount: discount
      });

      // 3) Update discount as percent with full precision (UI can round on display)
      setDiscount(requiredDiscountPercent);
      applyDiscountToProducts(requiredDiscountPercent);
      
      // 4) Mark that discount has been manually calculated
      setHasManualDiscountCalculation(true);
      // applyDiscountToProducts(requiredDiscountPercent);
      
      console.log('[EstimateFormV2] Discount updated and applied to products successfully');
    } catch (e) {
      console.log('[EstimateFormV2] Error in discount calculation:', e);
      // Fallback: keep previous discount if any error
    }
  };

  // Estimate navigation handlers
  const handleFirst = () => {
    if (currentEstimateIndex > 0 && sortedEstimates.length > 0) {
      const firstEstimate = sortedEstimates[0];
      const num = firstEstimate?.uniqueData?.estimate_number;
      if (num) {
        navigate(`/estimates/v2`, { state: { estimateNumber: num }, replace: true });
      }
    }
  };

  const handlePrev = () => {
    if (currentEstimateIndex > 0 && sortedEstimates.length > 0) {
      const prevEstimate = sortedEstimates[currentEstimateIndex - 1];
      const num = prevEstimate?.uniqueData?.estimate_number;
      if (num) {
        navigate(`/estimates/v2`, { state: { estimateNumber: num }, replace: true });
      }
    }
  };

  const handleNext = () => {
    if (currentEstimateIndex < totalEstimates - 1 && sortedEstimates.length > 0) {
      const nextEstimate = sortedEstimates[currentEstimateIndex + 1];
      const num = nextEstimate?.uniqueData?.estimate_number;
      if (num) {
        navigate(`/estimates/v2`, { state: { estimateNumber: num }, replace: true });
      }
    }
  };

  const handleLast = () => {
    if (currentEstimateIndex < totalEstimates - 1 && sortedEstimates.length > 0) {
      const lastEstimate = sortedEstimates[totalEstimates - 1];
      const num = lastEstimate?.uniqueData?.estimate_number;
      if (num) {
        navigate(`/estimates/v2`, { state: { estimateNumber: num }, replace: true });
      }
    }
  };

  const loadEstimateByIndex = async (index) => {
    const target = sortedEstimates[index];
    const num = target?.uniqueData?.estimate_number;
    if (num) {
      navigate(`/estimates/v2`, { state: { estimateNumber: num }, replace: true });
    }
  };

  const fetchAllEstimates = useCallback(async () => {
    try {
      // Mirror v1: use /Estimate/summary then filter last 24h and sort
      const response = await axios.get(`${baseURL}/Estimate/summary`);
      const estimates = response.data.summary || [];

      // Filter to last 24 hours (same as v1)
      const filtered = estimates

      // Sort by date desc, then by estimate number desc
      const sorted = filtered.sort((a, b) => {
        const dateA = new Date(a.uniqueData?.date);
        const dateB = new Date(b.uniqueData?.date);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateB.getTime() - dateA.getTime();
        }
        const numA = parseInt(String(a.uniqueData?.estimate_number || '').replace(/[^0-9]/g, '')) || 0;
        const numB = parseInt(String(b.uniqueData?.estimate_number || '').replace(/[^0-9]/g, '')) || 0;
        return numB - numA;
      });

      setSortedEstimates(sorted);
      setAllEstimates(sorted.map(s => ({ estimate_number: s.uniqueData?.estimate_number })));
      setTotalEstimates(sorted.length);

      // Find current index
      const currentIndex = sorted.findIndex(
        est => String(est.uniqueData?.estimate_number) === String(estimate_number)
      );
      if (currentIndex !== -1) {
        setCurrentEstimateIndex(currentIndex);
      }
    } catch (error) {
      console.error('Error fetching estimates summary:', error);
      setSortedEstimates([]);
      setAllEstimates([]);
      setTotalEstimates(1);
      setCurrentEstimateIndex(0);
    }
  }, [baseURL, estimate_number]);

  // Function to generate the next estimate number with concurrent request handling
  const generateNextEstimateNumber = useCallback(async () => {
    try {
      // Fetch all estimates to find the highest number
      const response = await axios.get(`${baseURL}/Estimate/summary`);
      const estimates = response.data.summary || [];
      
      // Extract all estimate numbers and find the highest
      const estimateNumbers = estimates
        .map(est => est.uniqueData?.estimate_number)
        .filter(num => num && num.startsWith('EST-'))
        .map(num => {
          const match = num.match(/EST-(\d+)/);
          return match ? parseInt(match[1]) : 0;
        })
        .filter(num => !isNaN(num) && num > 0); // Ensure only positive numbers
      
      // Find the next number (always start from 1 if no estimates exist or if highest is 0)
      let nextNumber = 1;
      if (estimateNumbers.length > 0) {
        const highestNumber = Math.max(...estimateNumbers);
        // Only increment if the highest number is greater than 0
        if (highestNumber > 0) {
          nextNumber = highestNumber + 1;
        }
      }
      

      
      console.log('[EstimateFormV2] Estimate number generation:', {
        totalEstimates: estimates.length,
        estimateNumbers,
        highestNumber: estimateNumbers.length > 0 ? Math.max(...estimateNumbers) : 0,
        nextNumber,
        nextEstimateNumber: `EST-${nextNumber}`
      });
      
      // Ensure the next number is always at least 1
      const finalNumber = Math.max(1, nextNumber);
      
      console.log('[EstimateFormV2] Final estimate number:', {
        calculatedNextNumber: nextNumber,
        finalNumber,
        finalEstimateNumber: `EST-${finalNumber}`
      });
      
      return `EST-${finalNumber}`;
    } catch (error) {
      console.error('Error generating estimate number:', error);
      // Fallback to EST-1 if API fails
      return `EST-1`;
    }
  }, [baseURL]);

  useEffect(() => {
    const incomingOrderDetails = location.state?.orderDetails;
    if (incomingOrderDetails && incomingOrderDetails.length > 0) {
      const item = incomingOrderDetails[0];

      setProductFormData((prev) => ({
        ...prev,
        ...item, 
      }));
    }
  }, [location.state]);

  // Generate estimate number when component mounts (if not editing)
  useEffect(() => {
    const initializeEstimateNumber = async () => {
      // Check if this is a new estimate request from state
      const isNewEstimateFromState = location.state?.isNewEstimate === true;
      
      // Check if we should create a new estimate (no estimate number or invalid one)
      const shouldCreateNewEstimate = !estimateFormData.estimate_number || 
                                     estimateFormData.estimate_number === 'EST0' ||
                                     estimateFormData.estimate_number === 'EST-0' ||
                                     isNewEstimateFromState ||
                                     sessionStorage.getItem('createNewEstimate') === 'true';
      
      if (!isEditMode && shouldCreateNewEstimate) {
        console.log('[EstimateFormV2] Initializing estimate number, isNewEstimate:', isNewEstimateFromState, 'shouldCreateNew:', shouldCreateNewEstimate);
        
        // Clear the form data if it's a new estimate request or invalid estimate number
        if (isNewEstimateFromState || !estimateFormData.estimate_number || estimateFormData.estimate_number === 'EST0' || sessionStorage.getItem('createNewEstimate') === 'true') {
          console.log('[EstimateFormV2] Clearing form data for new estimate');
          
          // Clear the sessionStorage flag
          sessionStorage.removeItem('createNewEstimate');
          
          setEstimateFormData(prev => ({
            ...prev,
            estimate_number: '',
            date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })
          }));
          
          // Clear other form states
          setCustomerFormData({
            customer_id: "",
            customer_name: "",
            account_name: "",
            mobile: "",
            address: "",
            address1: "",
            address2: "",
            city: "",
            pincode: "",
            state: "",
            state_code: "",
            gst_no: "",
            gst_in: "",
            email: "",
            aadhar_card: "",
            pan_card: "",
          });
          
          // Clear manual mode flags
          setIsManualNetMode(false);
          setHasUserEditedNetPay(false);
          setManualNetPayAmount(0);
          
          // Clear other data
          setOldSalesData([]);
          setOldTableData([]);
          setSaleReturnData([]);
        }
        
        const nextEstimateNumber = await generateNextEstimateNumber();
        setEstimateFormData(prev => ({
          ...prev,
          estimate_number: nextEstimateNumber
        }));
        
        console.log('[EstimateFormV2] Estimate number initialized:', nextEstimateNumber);
      }
    };
    
    initializeEstimateNumber();
  }, [isEditMode, estimateFormData.estimate_number, location.state?.isNewEstimate, generateNextEstimateNumber, navigate]);

  // Function to reset estimate number for new estimates
  const resetEstimateNumber = useCallback(async () => {
    if (!isEditMode) {
      const nextEstimateNumber = await generateNextEstimateNumber();
      setEstimateFormData(prev => ({
        ...prev,
        estimate_number: nextEstimateNumber
      }));
    }
  }, [isEditMode, generateNextEstimateNumber]);

  // Fetch current rates using hook
  useEffect(() => {
    if (!isEditMode) {
    fetchCurrentRates(baseURL);
    }
  }, [fetchCurrentRates, isEditMode]);

  // Auto-recalculate rate when purity changes using hook
  useEffect(() => {
    if (!isEditMode) {
    autoCalculateRate();
    }
  }, [autoCalculateRate, isEditMode]);

  // Auto-select default purity when metal type changes using hook
  useEffect(() => {
    if (!isEditMode) {
    autoSelectDefaultPurity(purityOptions);
    }
  }, [autoSelectDefaultPurity, purityOptions, isEditMode]);

  // Update metal type options when metal type is set from barcode
  useEffect(() => {
    if (allMetalTypes.length > 0) {
      // Show all available metal types, not just the filtered ones
      const metalOptions = allMetalTypes
        .filter(metal => metal) // Remove any null/undefined values
        .map(metal => ({
          value: metal,
          label: metal,
        }));
      
      setMetaltypeOptions(metalOptions);
    }
  }, [allMetalTypes]);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (!isEditMode) {
    fetchSubCategory();
    }
  }, [productFormData.category, isEditMode]);

  // Filter metal types based on selected category
  useEffect(() => {
    if (productFormData.category) {
      const selectedProduct = products.find(
        (product) => product.product_name === productFormData.category
      );
      
      if (selectedProduct && selectedProduct.Category) {
        const filteredMetals = allMetalTypes.filter((metal) =>
          metal && metal.toLowerCase() === selectedProduct.Category.toLowerCase()
        );
        
        const metalOptions = filteredMetals.map((metal) => ({
          value: metal,
          label: metal,
        }));
        
        setMetaltypeOptions(metalOptions);
        
        if (metalOptions.length > 0 && !productFormData.metal_type) {
          setProductFormData((prevData) => ({
            ...prevData,
            metal_type: metalOptions[0].value,
          }));
        }
      }
    }
  }, [productFormData.category, products, allMetalTypes]);

  // Filter subcategory based on selected category
  useEffect(() => {
    if (productFormData.category) {
      const selectedCategory = allSubcategory.find(
        (item) => item.category === productFormData.category
      );

      if (selectedCategory) {
        const filteredSubcats = allSubcategory.filter(
          (subcat) => subcat.category === selectedCategory.category
        );

        const subcatOptions = filteredSubcats.map((item) => ({
          label: item.sub_category_name,
          value: item.sub_category_name,
          selling_purity: item.selling_purity,
          printing_purity: item.printing_purity,
          purity: item.purity,
          pricing: item.pricing,
          pricingOpt: item.pricingOpt,
        }));

        setSubcategoryOptions(subcatOptions);

        if (subcatOptions.length > 0 && !productFormData.product_name) {
          setProductFormData((prevData) => ({
            ...prevData,
          }));
        }
      }
    }
  }, [productFormData.category, allSubcategory]);


  // Fetch all data on component mount
  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    fetchData();
    fetchCategory();
    fetchMetalType();
    fetchSubCategory();
    fetchDesignOptions();
    fetchPurityOptions();
    if (!isEditMode) {
      fetchFestivalOffers();
    }
    fetchAllEstimates();
  }, [isEditMode, fetchAllEstimates]);

  // Update estimate form data when estimate number changes
  useEffect(() => {
    if (estimate_number) {
      setEstimateFormData(prev => ({
        ...prev,
        estimate_number: estimate_number,
        date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })
      }));
    }
  }, [estimate_number]);

  // Sync estimate number from route state so navigation works without reload
  useEffect(() => {
    const routedEstimate = location.state?.estimateNumber;
    if (routedEstimate && routedEstimate !== estimateFormData.estimate_number) {
      setEstimateFormData(prev => ({
        ...prev,
        estimate_number: routedEstimate,
      }));
    }
  }, [location.state?.estimateNumber]);

  // Update current estimate index when estimate number or list changes
  useEffect(() => {
    if (estimate_number && sortedEstimates.length > 0) {
      const currentIndex = sortedEstimates.findIndex(
        est => String(est.uniqueData?.estimate_number) === String(estimate_number)
      );
      if (currentIndex !== -1) {
        setCurrentEstimateIndex(currentIndex);
      }
    }
  }, [estimate_number, sortedEstimates]);

  // Get all products for display
  const allProducts = getAllProducts();

  // Auto-apply offers when both appliedOffersFromAPI and products are ready
  useEffect(() => {
    if (isEditMode) return;
    if (appliedOffersFromAPI.length > 0 && allProducts.length > 0) {
      appliedOffersFromAPI.forEach((offer) => {
        const alreadyApplied = appliedOffers.some(
          (applied) => (applied.offer_id || applied._id) === (offer.offer_id || offer._id)
        );
        if (!alreadyApplied) {
          applyFestivalDiscountToAllProducts(offer);
          setAppliedOffers(prev => [...prev, offer]);
          setIsAnyOfferApplied(true);
        }
      });
    }
  }, [appliedOffersFromAPI, allProducts, appliedOffers, applyFestivalDiscountToAllProducts, isEditMode]);

  // Use the estimate calculations hook
  const estimateCalculations = useEstimateCalculations({
    allProducts: allProducts,
    oldSalesData, // live URD form entries
    oldTableData, // table mirror
    schemeSalesData,
    schemeTableData,
    saleReturnData,
    discount,
    manualNetPayAmount,
    manualChangeSeq,
    // Enable manual path when manual mode is active
    isManualNetMode: isManualNetMode,
    hasManualDiscountCalculation,
    suppressReverseOnce,
    taxRate: 3, // Assuming 3% GST
  });





  // Extract calculated values for PaymentDetailsV2
  const {
    totalAmount,
    taxableAmount,
    discountAmt,
    festivalDiscountAmt: calculatedFestivalDiscountAmt,
    taxAmount,
    netAmount,
    oldItemsAmount,
    schemeAmount,
    salesAmountToPass,
    netPayableAmount,
    effectiveDiscountPercentage,
  } = estimateCalculations;

  // Festival discount is now calculated in computeEstimateTotals function
  // Update festival discount amount from calculations
  useEffect(() => {
    setFestivalDiscountAmt(calculatedFestivalDiscountAmt || 0);
  }, [calculatedFestivalDiscountAmt]);

  // Update discount percentage only when user manually calculated it
  // Prevent URD/SR selections from changing discount implicitly
  useEffect(() => {
    if (
      isManualNetMode &&
      hasUserEditedNetPay &&
      hasManualDiscountCalculation &&
      effectiveDiscountPercentage !== discount
    ) {
      setDiscount(effectiveDiscountPercentage);
    }
  }, [
    effectiveDiscountPercentage,
    isManualNetMode,
    hasUserEditedNetPay,
    hasManualDiscountCalculation,
    discount,
  ]);

  // Update manual net payable amount when products change in manual mode
  useEffect(() => {
    if (isManualNetMode && hasUserEditedNetPay) {
      // When in manual mode and products change, update the manual amount to reflect new calculation
      const newManualAmount = Math.round(netPayableAmount);
      console.log('[EstimateFormV2] Checking if manual amount needs update:', {
        isManualNetMode,
        hasUserEditedNetPay,
        oldManualAmount: manualNetPayAmount,
        newManualAmount,
        netPayableAmount,
        productCount: allProducts.length,
        needsUpdate: newManualAmount !== manualNetPayAmount
      });
      
      // If the change is immediately after a direct manual entry, skip one sync to avoid bounce
      if (suppressNextManualSyncRef.current) {
        suppressNextManualSyncRef.current = false;
        return;
      }

      if (newManualAmount !== manualNetPayAmount) {
        console.log('[EstimateFormV2] Products changed in manual mode, updating manual amount:', {
          oldManualAmount: manualNetPayAmount,
          newManualAmount,
          netPayableAmount,
          productCount: allProducts.length
        });
        setManualNetPayAmount(newManualAmount);
      }
    }
  }, [netPayableAmount, isManualNetMode, hasUserEditedNetPay, manualNetPayAmount, allProducts.length]);

  // Debug manual mode state changes
  useEffect(() => {
    console.log('[EstimateFormV2] Manual mode state changed:', {
      isManualNetMode,
      hasUserEditedNetPay,
      manualNetPayAmount,
      netPayableAmount
    });
  }, [isManualNetMode, hasUserEditedNetPay, manualNetPayAmount, netPayableAmount]);

  // Enable manual editing when URD purchase or sale return changes
  useEffect(() => {
    // Check if there are any URD purchases or sales returns
    const hasURDPurchases = oldItemsAmount > 0;
    const hasSalesReturns = salesAmountToPass > 0;

    console.log('[EstimateFormV2] URD/SR useEffect triggered:', {
      hasURDPurchases,
      hasSalesReturns,
      oldItemsAmount,
      salesAmountToPass,
      hasUserEditedNetPay,
      isManualNetMode,
      manualNetPayAmount,
      netPayableAmount
    });

    if (hasURDPurchases || hasSalesReturns) {
      // When URD purchases or sales returns are present, only reset to calculated mode
      // if user hasn't manually edited the net payable amount
      if (!hasUserEditedNetPay) {
        console.log('[EstimateFormV2] URD/SR present, user has not edited - resetting to calculated mode');
        setIsManualNetMode(false);
        setHasUserEditedNetPay(false);
        setManualNetPayAmount(0);
      } else {
        console.log('[EstimateFormV2] URD/SR present, but user has edited - updating manual amount for new URD/SR values');
        // User has edited, but URD/SR amounts changed - recalculate the manual amount
        // to maintain the same net payable amount with the new deductions
        const newNetPayable = netAmount - oldItemsAmount - salesAmountToPass;
        setSuppressReverseOnce(true);
        setManualNetPayAmount(Math.round(newNetPayable));
        console.log('[EstimateFormV2] Updated manual amount:', {
          oldManualAmount: manualNetPayAmount,
          newManualAmount: Math.round(newNetPayable),
          netAmount,
          oldItemsAmount,
          salesAmountToPass,
          calculatedNetPayable: newNetPayable
        });
      }
    } else {
      // When there are no deductions, preserve manual override if the user edited it
      if (hasUserEditedNetPay) {
        console.log('[EstimateFormV2] No deductions, but user edited - enabling manual mode');
        setIsManualNetMode(true);
      } else {
        console.log('[EstimateFormV2] No deductions, no user edit - calculated mode');
        setIsManualNetMode(false);
        setManualNetPayAmount(0);
      }
    }
  }, [oldItemsAmount, salesAmountToPass, hasUserEditedNetPay, isEditMode]);

  // Reset manual flags when all sales return items are removed
  useEffect(() => {
    const currentCount = Array.isArray(saleReturnData) ? saleReturnData.length : 0;
    if (lastSaleReturnCount > 0 && currentCount === 0) {
      // All sales return items removed: revert to computed mode
      setIsManualNetMode(false);
      setHasUserEditedNetPay(false);
      setManualNetPayAmount(0);
    }
    setLastSaleReturnCount(currentCount);
  }, [saleReturnData, lastSaleReturnCount, hasUserEditedNetPay]);

  // Reset suppression flag after programmatic manual amount update has propagated
  useEffect(() => {
    if (suppressReverseOnce) {
      const t = setTimeout(() => setSuppressReverseOnce(false), 0);
      return () => clearTimeout(t);
    }
  }, [manualNetPayAmount, suppressReverseOnce]);

  // Reset manual mode when products are added or removed (but only if no URD/SR deductions)
  useEffect(() => {
    const hasURDPurchases = oldItemsAmount > 0;
    const hasSalesReturns = salesAmountToPass > 0;
    
    // Only reset manual mode if there are no deductions present AND user hasn't manually set net payable
    // Don't reset if user has manually entered a net payable amount - preserve their input
    if (!hasURDPurchases && !hasSalesReturns && hasUserEditedNetPay && manualNetPayAmount === 0) {

      // Reset manual mode when products change and no deductions are present
      setIsManualNetMode(false);
      setHasUserEditedNetPay(false);
      setManualNetPayAmount(0);
    }
  }, [allProducts.length, hasUserEditedNetPay, oldItemsAmount, salesAmountToPass, manualNetPayAmount]);

  // If URD/SR amounts change, only reset manual override if no manual amount is set
  useEffect(() => {
    if (hasUserEditedNetPay && manualNetPayAmount === 0) {
      setHasUserEditedNetPay(false);
    }
  }, [oldItemsAmount, salesAmountToPass, hasUserEditedNetPay, manualNetPayAmount]);

  // If editing, load full details and populate products/URD/sales return similarly to v1
  useEffect(() => {
    const loadExisting = async () => {
      if (!estimate_number) return;
      
      // Don't load existing data if this is a new estimate
      const isNewEstimateFromState = location.state?.isNewEstimate === true;
      if (isNewEstimateFromState) {
        console.log('[EstimateFormV2] Skipping loadExisting for new estimate');
        return;
      }
      
      // Set loading flag to prevent discount recalculation during load
      setIsLoadingExistingEstimate(true);
      
      try {
        // First try to get the date from the summary endpoint (same as table)
        let estimateDate = null;
        try {
          const summaryResponse = await axios.get(`${baseURL}/Estimate/summary`);
          const summaryData = summaryResponse.data.summary || [];
          const matchingEstimate = summaryData.find(est => est.uniqueData?.estimate_number === estimate_number);
          if (matchingEstimate?.uniqueData?.created_at) {
            estimateDate = matchingEstimate.uniqueData.created_at;
            console.log('[EstimateFormV2] Found date from summary endpoint:', estimateDate);
          }
        } catch (summaryError) {
          console.log('[EstimateFormV2] Could not get date from summary endpoint:', summaryError);
        }

        // Always load full-details for other data, but use date from summary if available
        const response = await axios.get(`${baseURL}/Estimate/full-details/${estimate_number}`);
        const data = response.data;
        if (!data) return;

        // If we didn't get the date from summary, try the full-details endpoint
        if (!estimateDate) {
          // Debug: Log the API response structure to understand date fields
          console.log('[EstimateFormV2] API Response structure:', {
            hasDate: !!data.date,
            dateValue: data.date,
            hasCreatedAt: !!data.created_at,
            createdAtValue: data.created_at,
            hasEstimateDate: !!data.estimate_date,
            estimateDateValue: data.estimate_date,
            allKeys: Object.keys(data)
          });

          // Try multiple possible date fields from the API response
          estimateDate = data.date || data.created_at || data.estimate_date || data.uniqueData?.created_at;
          console.log('[EstimateFormV2] Selected date from full-details:', estimateDate);
        }

        // Update estimate form data
        setEstimateFormData(prev => ({
          ...prev,
          estimate_number: estimate_number,
          date: estimateDate ? new Date(estimateDate).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })
        }));

        // Populate customer header minimal (optional in v2)
        if (data.account) {
          setCustomerFormData((prev) => ({
            ...prev,
            customer_id: data.account.account_id || "",
            customer_name: data.account.account_name || "",
            account_name: data.account.account_name || "",
            mobile: data.account.phone || data.account.mobile || "",
            address1: data.account.address1 || "",
            address2: data.account.address2 || "",
            city: data.account.city || "",
            pincode: data.account.pincode || "",
            state: data.account.state || "",
            state_code: data.account.state_code || "",
            aadhar_card: data.account.aadhar_card || "",
            gst_in: data.account.gst_in || "",
            pan_card: data.account.pan_card || "",
          }));
        }

        // Map products into v2 shape and load via hook
        const mappedProducts = (data.products || []).map((product) => {
          const grossWeight = parseFloat(product.gr_wt) || 0;
          const stoneWeight = parseFloat(product.st_wt) || 0;
          const weightBW = grossWeight - stoneWeight;
          const vaPercent = parseFloat(product.va_perc) || 0;
          const wastageWeight = (grossWeight * vaPercent) / 100;
          const totalWeightAV = weightBW + wastageWeight;

          // Prefer persisted values from backend instead of recomputing
          const rate = parseFloat(product.rate) || 0;
          const makingCharges = parseFloat(product.mc) || 0;
          const stonePrice = parseFloat(product.stone_price) || 0;
          const hmCharges = parseFloat(product.hm_charges) || 60;
          const discount = parseFloat(product.discount) || 0;
          const festivalDiscount = parseFloat(product.fest_discount) || 0;
          const taxPercent = parseFloat(product.tax_perc) || 3;

          // Use backend totals directly
          const taxAmt = parseFloat(product.tax_amt) || 0;
          const totalPrice = parseFloat(product.total_price) || 0;
          const rateAmt = parseFloat(product.rate_amt) || rate * totalWeightAV;

          // Calculate the original taxable amount before discount
          // piece_taxable_amt should be the taxable amount AFTER discount
          // original_piece_taxable_amt should be the taxable amount BEFORE discount
          const pieceTaxableAmt = totalPrice - taxAmt; // Current taxable amount (after discount)
          const originalPieceTaxableAmt = pieceTaxableAmt + discount; // Original taxable amount (before discount)

          return {
            id: product.id || Date.now(),
            code: product.barcode || "",
            product_name: product.product_name || "",
            metal_type: product.metal || "",
            purity: product.purity || "",
            gross_weight: grossWeight.toFixed(2),
            stone_weight: stoneWeight.toFixed(2),
            va_percent: vaPercent.toFixed(2),
            total_weight_av: totalWeightAV.toFixed(2),
            weight_bw: weightBW.toFixed(2),
            wastage_weight: wastageWeight.toFixed(2),
            rate: rate.toFixed(2),
            rate_amt: rateAmt.toFixed(2),
            making_charges: makingCharges.toFixed(2),
            stone_price: stonePrice.toFixed(2),
            hm_charges: hmCharges.toFixed(2),
            disscount: discount.toFixed(2), // Use the saved discount from database
            festival_discount: festivalDiscount.toFixed(2),
            tax_percent: `${taxPercent}% GST`,
            tax_amt: taxAmt.toFixed(2),
            total_price: totalPrice.toFixed(2),
            imagePreview: product.image || "",
            qty: (product.qty || 1).toString(),
            category: product.category || "",
            sub_category: product.sub_category || "",
            design_name: product.design_name || "",
            va_on: "Gross Weight",
            mc_on: product.mc_on || "MC %",
            mc_per_gram: (product.mc_per_gram || 0).toString(),
            pricing: "By Weight",
            piece_taxable_amt: pieceTaxableAmt.toFixed(2), // Taxable amount after discount
            original_piece_taxable_amt: originalPieceTaxableAmt.toFixed(2), // Taxable amount before discount
            sale_status: "Not Delivered",
            estimate_status: "Not Delivered",
            transaction_status: "Sales",
          };
        });

        if (mappedProducts.length > 0) {
          console.log('[EstimateFormV2] Loading products from database:', {
            productCount: mappedProducts.length,
            firstProductDiscount: mappedProducts[0]?.disscount,
            isLoadingExistingEstimate
          });
          loadProducts(mappedProducts);
        }

        // Load URD items into oldSalesData/oldTableData for calculations
        try {
          const urdRes = await axios.get(`${baseURL}/get/olditems/${estimate_number}`);
          const urdItems = Array.isArray(urdRes.data) ? urdRes.data : [];
          setOldSalesData(urdItems);
          setOldTableData(urdItems);
        } catch (e) {
          setOldSalesData([]);
          setOldTableData([]);
        }

        // Load Sales Return items
        try {
          const srRes = await axios.get(`${baseURL}/get-estimate-sale-returns/${estimate_number}`);
          const srItems = Array.isArray(srRes.data) ? srRes.data : [];
          setSaleReturnData(srItems);
        } catch (e) {
          setSaleReturnData([]);
        }

        // Load header totals to reflect exact saved values
        try {
          const headRes = await axios.get(`${baseURL}/Estimate/estimate/${estimate_number}`);
          const h = headRes.data || {};
          
          // Set the estimate ID for future updates
          if (h.id) {
            setEstimateId(h.id);
          }
          
          const totals = {
            totalAmount: parseFloat(h.total_amt) || 0,
            discountAmt: parseFloat(h.discount_amt) || 0,
            taxableAmount: parseFloat(h.taxable_amt) || 0,
            taxAmount: parseFloat(h.tax_amt) || 0,
            netAmount: parseFloat(h.net_amt) || 0,
            netPayableAmount: parseFloat(h.net_payable_amt) || 0,
            oldItemsAmount: parseFloat(h.old_items_amt) || 0,
            schemeAmount: parseFloat(h.scheme_amt) || 0,
            salesAmountToPass: parseFloat(h.sale_return_amt) || 0,
          };
          setHeaderTotals(totals);

                      // Initialize discount state to the saved discount percentage if available, otherwise calculate from amount
            if (h.discount_percent !== undefined && h.discount_percent !== null) {
              setDiscount(parseFloat(h.discount_percent));
            } else {
              // Calculate discount percentage from the original taxable amount (before discount)
              const taxableAmount = parseFloat(h.taxable_amt) || 0;
              const disAmt = parseFloat(h.discount_amt) || 0;
              const originalTaxableAmount = taxableAmount + disAmt; // Add back the discount to get original amount
              const savedPercent = originalTaxableAmount > 0 ? (disAmt / originalTaxableAmount) * 100 : 0;
              setDiscount(parseFloat(savedPercent.toFixed(2)));
            }

          // Sync festival discount summary from header if available
          if (h.fest_discount_amt !== undefined && h.fest_discount_amt !== null) {
            setFestivalDiscountAmt(parseFloat(h.fest_discount_amt) || 0);
          }
        } catch (e) {
          // ignore header fetch failures, UI will fall back to computed values from products
        }
 
      } catch (e) {
        console.error("Error loading existing estimate:", e);
      }
      
      // Clear loading flag after ALL async operations are complete
      console.log('[EstimateFormV2] All loading operations complete, clearing isLoadingExistingEstimate flag');
      setIsLoadingExistingEstimate(false);
    };
    loadExisting();
    
    // Also fetch all estimates for navigation
    fetchAllEstimates();
  }, [estimate_number, loadProducts, fetchAllEstimates]);



  // Reload the estimate when the estimate number in form state changes
  useEffect(() => {
    if (!estimateFormData.estimate_number) return;
  }, [estimateFormData.estimate_number]);

    // Update product discount fields in real-time when estimate-level discount changes
  useEffect(() => {
    // Only run this when discount changes and products are present, and not in manual net mode
    // Also skip if user has manually edited the net payable amount or if discount has been manually calculated
    // Also skip if we're loading an existing estimate to prevent overwriting saved discounts
    if (discount > 0 && allProducts.length > 0 && !isManualNetMode && !hasUserEditedNetPay && !hasManualDiscountCalculation && !isLoadingExistingEstimate) {
      // Get current products and update their discount fields
      const currentProducts = allProducts;
      if (currentProducts.length > 0) {
        // Check if products already have the correct discount applied to prevent infinite loop
        const firstProduct = currentProducts[0];
        const currentDiscount = parseFloat(firstProduct.disscount || 0);
        
        // Use the same taxable base calculation as the estimate calculation for consistency
        const totalTaxableBase = currentProducts.reduce((sum, product) => {
          // Prefer original_piece_taxable_amt if available (before discount)
          const originalPieceTaxable = parseFloat(product.original_piece_taxable_amt) || 0;
          const currentPieceTaxable = parseFloat(product.piece_taxable_amt) || 0;
          
          if (originalPieceTaxable > 0) {
            return sum + originalPieceTaxable;
          } else if (currentPieceTaxable > 0) {
            return sum + currentPieceTaxable;
          } else {
            // Fallback: derive from total_price by removing tax (3% GST)
            const totalPrice = parseFloat(product.total_price) || 0;
            if (totalPrice > 0) {
              const derivedTaxable = totalPrice / (1 + (3 / 100)); // 3% GST
              return sum + derivedTaxable;
            }
          }
          return sum;
        }, 0);
        
        // Calculate expected discount using the same logic as the estimate calculation
        const firstProductTaxableBase = (() => {
          const originalPieceTaxable = parseFloat(firstProduct.original_piece_taxable_amt) || 0;
          const currentPieceTaxable = parseFloat(firstProduct.piece_taxable_amt) || 0;
          
          if (originalPieceTaxable > 0) {
            return originalPieceTaxable;
          } else if (currentPieceTaxable > 0) {
            return currentPieceTaxable;
          } else {
            const totalPrice = parseFloat(firstProduct.total_price) || 0;
            if (totalPrice > 0) {
              return totalPrice / (1 + (3 / 100)); // 3% GST
            }
          }
          return 0;
        })();
        
        const expectedDiscount = totalTaxableBase > 0 ? 
          (firstProductTaxableBase / totalTaxableBase) * (totalTaxableBase * parseFloat(discount || 0) / 100) : 0;
        
        // Only update if the discount is significantly different (avoid infinite loop)
        // Also check if products already have valid discounts that should be preserved
        const totalExistingDiscount = currentProducts.reduce((sum, product) => sum + (parseFloat(product.disscount || 0)), 0);
        const hasExistingDiscounts = totalExistingDiscount > 0;
        
        if (Math.abs(currentDiscount - expectedDiscount) > 0.01 && !hasExistingDiscounts) {
          // Use the same discount calculation logic as applyDiscountToProducts
          const discountAmount = (totalTaxableBase * parseFloat(discount || 0)) / 100;

          // Update products with new discount distribution
          const productsWithUpdatedDiscount = currentProducts.map(product => {
            // Use the same taxable base calculation as above for consistency
            const originalPieceTaxable = parseFloat(product.original_piece_taxable_amt) || 0;
            const currentPieceTaxable = parseFloat(product.piece_taxable_amt) || 0;
            
            let productTaxableBase = 0;
            if (originalPieceTaxable > 0) {
              productTaxableBase = originalPieceTaxable;
            } else if (currentPieceTaxable > 0) {
              productTaxableBase = currentPieceTaxable;
            } else {
              // Fallback: derive from total_price
              const totalPrice = parseFloat(product.total_price) || 0;
              if (totalPrice > 0) {
                productTaxableBase = totalPrice / (1 + (3 / 100)); // 3% GST
              }
            }
            
            let productDiscount = 0;
            if (productTaxableBase > 0 && totalTaxableBase > 0) {
              productDiscount = (productTaxableBase / totalTaxableBase) * discountAmount;
            }

            return {
              ...product,
              disscount: parseFloat(productDiscount).toFixed(2),
            };
          });

          // Update the products state to reflect the new discount
          console.log('[EstimateFormV2] Updating product discounts due to discount percentage change:', {
            discount,
            currentDiscount: currentDiscount,
            expectedDiscount: expectedDiscount,
            isLoadingExistingEstimate,
            totalExistingDiscount,
            hasExistingDiscounts,
            willUpdate: Math.abs(currentDiscount - expectedDiscount) > 0.01 && !hasExistingDiscounts
          });
          loadProducts(productsWithUpdatedDiscount);
        }
      }
    }
  }, [discount, allProducts, loadProducts, isManualNetMode, hasUserEditedNetPay, hasManualDiscountCalculation, isLoadingExistingEstimate]);

  return (
    <div className="main-container">
      <Container className="sales-form-container">
        <Form>
          {/* Customer Details Section */}
          <div className="sales-form" style={{ marginTop: "-40px" }}>
            <div className="sales-form-left">
              <CustomerDetails
                formData={customerFormData}
                setFormData={setCustomerFormData}
                handleAddCustomer={handleAddCustomer}
                customers={customers}
                setSelectedMobile={setSelectedMobile}
                mobileRef={mobileRef}
                isEstimatePage={true}
                handleCustomerChange={handleCustomerChange}
              />
            </div>
            <div className="sales-form-right">
              <EstimateDetails
                formData={estimateFormData}
                setFormData={setEstimateFormData}
                currentEstimateIndex={currentEstimateIndex}
                totalEstimates={totalEstimates}
                onPrev={handlePrev}
                onNext={handleNext}
                onFirst={handleFirst}
                onLast={handleLast}
              />
            </div>
          </div>
          
          {/* Product Form Section */}
          <ProductForm
            formData={estimateFormData}
            productFormData={productFormData}
            setProductFormData={setProductFormData}
            handleChange={handleChange}
            handleAdd={handleAdd}
            handleUpdate={handleUpdate}
            isEditing={isEditing}
            editingIndex={editingIndex}
            calculations={calculations}
            startEditing={startEditing}
            cancelEditing={cancelEditing}
            handleDeleteProduct={handleDeleteProduct}
            getAllProducts={getAllProducts}
            updateFormDataWithCalculations={updateFormDataWithCalculations}
            handleTotalPriceChange={handleTotalPriceChange}
            clearForm={clearForm}
            handleClear={clearForm}
            products={products}
            data={data}
            categoryOptions={categoryOptions}
            subcategoryOptions={subcategoryOptions}
            designOptions={designOptions}
            purityOptions={purityOptions}
            metaltypeOptions={metaltypeOptions}
            offers={offers}
            rates={rates}
            initialProductFormData={DEFAULT_PRODUCT_FORM_DATA}
            handleBarcodeChange={handleBarcodeChange}
            handleProductNameChange={handleProductNameChange}
            handleMetalTypeChange={handleMetalTypeChange}
            handleDesignNameChange={handleDesignNameChange}
            handleImageChange={handleImageChange}
            fileInputRef={fileInputRef}
            clearImage={clearImage}
            captureImage={captureImage}
            setShowWebcam={setShowWebcam}
            showWebcam={showWebcam}
            webcamRef={webcamRef}
            setShowOptions={setShowOptions}
            showOptions={showOptions}
            isQtyEditable={true}
            isManualTotalPriceChange={isManualTotalPriceChange}
            setIsManualTotalPriceChange={setIsManualTotalPriceChange}
            resetEstimateNumber={resetEstimateNumber}
          />
          
          {/* Product List Display */}
          <div className="product-list-section" style={{ marginTop: "20px", backgroundColor: "#ffffff" }}>
            <ProductTable
              estimate_number={estimate_number}
              products={allProducts}
              onDelete={handleDeleteProduct}
              onEdit={startEditing}
              estimate_date={estimateFormData.date}
            />
          </div>

          {/* Sales Form Section with all sub-components visible */}
          <div className="sales-form">
            <div className="sales-form-third">
              <Col className="sales-form-section">
                <Row style={{ height: "45px" }}>
                  <Col xs={12} className="mb-3">
                    <Button
                      style={{
                        backgroundColor: activeForm === "old" ? "rgb(163, 110, 41)" : "gray",
                        borderColor: activeForm === "old" ? "rgb(163, 110, 41)" : "gray",
                        color: "white", fontSize: '13px', padding: '5px'
                      }}
                      onClick={() => setActiveForm("old")}
                    >
                      URD Purchase
                    </Button>
                    <Button
                      style={{
                        backgroundColor: activeForm === "schemes" ? "rgb(163, 110, 41)" : "gray",
                        borderColor: activeForm === "schemes" ? "rgb(163, 110, 41)" : "gray",
                        color: "white", fontSize: '13px', padding: '5px'
                      }}
                      onClick={() => setActiveForm("schemes")}
                      className="ms-2"
                    >
                      Schemes
                    </Button>
                    <Button
                      style={{
                        backgroundColor: activeForm === "sale_return" ? "rgb(163, 110, 41)" : "gray",
                        borderColor: activeForm === "sale_return" ? "rgb(163, 110, 41)" : "gray",
                        color: "white", fontSize: '13px', padding: '5px'
                      }}
                      onClick={() => setActiveForm("sale_return")}
                      className="ms-2"
                    >
                      Sale Return
                    </Button>
                    <Button
                      style={{
                        backgroundColor: activeForm === "orders" ? "rgb(163, 110, 41)" : "gray",
                        borderColor: activeForm === "orders" ? "rgb(163, 110, 41)" : "gray",
                        color: "white", fontSize: '13px', padding: '5px'
                      }}
                      onClick={() => setActiveForm("orders")}
                      className="ms-2"
                    >
                      Orders
                    </Button>
                    <Button
                      style={{
                        backgroundColor: activeForm === "repair" ? "rgb(163, 110, 41)" : "gray",
                        borderColor: activeForm === "repair" ? "rgb(163, 110, 41)" : "gray",
                        color: "white", fontSize: '13px', padding: '5px'
                      }}
                      onClick={() => setActiveForm("repair")}
                      className="ms-2"
                    >
                      Repairs
                    </Button>
                  </Col>
                </Row>

                {/* All sub-components rendered based on activeForm */}
                {activeForm === "old" && (
                  <EstimateOldSalesForm 
                    setOldSalesData={setOldSalesData} 
                    oldTableData={oldTableData} 
                    setOldTableData={setOldTableData} 
                  />
                )}
                {activeForm === "schemes" && (
                  <EstimateSchemesForm
                    setSchemeSalesData={setSchemeSalesData}
                    selectedMobile={selectedMobile}
                  />
                )}
                {activeForm === "orders" && (
                  <EstimateOrdersTable
                    selectedMobile={selectedMobile || ""}
                    orderData={orderData || []}
                    handleCloseModal={handleCloseModal}
                    handleViewDetails={handleViewDetails}
                    selectedOrder={selectedOrder || []}
                    handleOrderCheckboxChange={handleOrderCheckboxChange}
                    showModal={showModal}
                    orderDetails={orderDetails || null}
                    loading={loading}
                    formatDate={formatDate}
                  />
                )}
                {activeForm === "repair" && (
                  <EstimateRepairsTable
                    selectedMobile={selectedMobile || ""}
                    repairs={repairs || []}
                    handleRepairCheckboxChange={handleRepairCheckboxChange}
                  />
                )}
                {activeForm === "sale_return" && (
                  <EstimateSaleReturnForm
                    setSaleReturnData={setSaleReturnData}
                    saleReturnData={saleReturnData}
                    selectedMobile={selectedMobile}
                  />
                )}
              </Col>
            </div>
              <PaymentDetailsV2
                estimate_number={estimate_number}
                totalAmount={isEditMode && headerTotals ? headerTotals.totalAmount : totalAmount}
                taxableAmount={isEditMode && headerTotals ? headerTotals.taxableAmount : taxableAmount}
                discountAmt={isEditMode && headerTotals ? headerTotals.discountAmt : discountAmt}
                taxAmount={isEditMode && headerTotals ? headerTotals.taxAmount : taxAmount}
                netAmount={isEditMode && headerTotals ? headerTotals.netAmount : netAmount}
                netPayableAmount={(() => {
                  // Always use the calculated netPayableAmount from the calculations hook
                  // This ensures that sales returns and URD purchases are properly reflected
                  const calculatedNetPayable = Math.round(netPayableAmount);
                  
                  // For edit mode with header totals, calculate from base net amount
                  if (isEditMode && headerTotals) {
                    const baseNet = parseFloat(headerTotals.netAmount || 0);
                    return Math.round(baseNet - oldItemsAmount - salesAmountToPass);
                  }
                  
                  // Return the calculated amount - PaymentDetailsV2 will handle manual mode display
                  return calculatedNetPayable;
                })()}
                oldItemsAmount={oldItemsAmount}
                schemeAmount={isEditMode && headerTotals ? headerTotals.schemeAmount : schemeAmount}
                salesAmountToPass={salesAmountToPass}
                paymentDetails={paymentDetails}
                setPaymentDetails={setPaymentDetails}
                saving={saving}
                loading={loading}
                festivalShowModal={festivalShowModal}
                handleSave={handleSave}
                handleBack={handleBack}
                handleFestivalShowModal={handleFestivalShowModal}
                handleFestivalCloseModal={handleFestivalCloseModal}
                handleApply={handleApply}
                offers={offers}
                appliedOffers={appliedOffers}
                setAppliedOffers={setAppliedOffers}
                festivalDiscountAmt={festivalDiscountAmt}
                isAnyOfferApplied={isAnyOfferApplied}
                discount={discount}
                handleDiscountChange={handleDiscountChange}
                handleManualNetPayAmountChange={handleManualNetPayAmountChange}
                isManualNetMode={isManualNetMode}
                manualNetPayAmount={manualNetPayAmount}
                resetAllFormData={resetAllFormData}
                />
          </div>
          
        </Form>
        {/* Hidden PDF Provider for auto-print after save */}
        <div style={{ display: "none" }}>
          <BlobProvider
            document={
              <EstimateA4
                formData={{
                  estimate: estimateFormData.estimate_number || estimate_number || "EST-000",
                  date: estimateFormData.date || new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
                  time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                  customerName: customerFormData.account_name || "",
                  mobile: customerFormData.mobile || "",
                }}
                items={allProducts
                  .filter((item) => item && item.product_name)
                  .map((item) => ({
                    categoty: `${item.category || 'General'}`,
                    item: `${item.product_name || 'Product'} (${item.metal_type || 'N/A'})`,
                    rate: item.rate || 0,
                    weight: item.total_weight_av
                      ? `${item.total_weight_av} GM`
                      : "0 GM",
                    vaPercent: item.va_percent || item.va_perc || item.wastage_percent || "0",
                    mcType: item.mc_on || "MC %",
                    mcValue: item.mc_per_gram || 0,
                    mc: `${item.making_charges || 0}`,
                    value: item.total_price || 0,
                    discount: item.disscount || 0,
                    remarks: item.remarks || "",
                    festivalDiscount: item.fest_discount || 0,
                    remark_type: item.remarks_type || "",
                    remark_value: item.remarks || "",
                    tax_amt: item.tax_amt || 0,
                    purity: item.purity || "",
                    printing_purity:item.printing_purity||"",
                  }))}
                subtotal={(() => {
                  // Use the same logic as EstimateTableV2
                  const calculatedSubtotal = allProducts.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
                  return calculatedSubtotal;
                })()}
                cgst={(() => {
                  // Use the same logic as EstimateTableV2
                  const taxAmountValue = parseFloat(taxAmount) || 0;
                  return taxAmountValue / 2;
                })()}
                sgst={(() => {
                  // Use the same logic as EstimateTableV2
                  const taxAmountValue = parseFloat(taxAmount) || 0;
                  return taxAmountValue / 2;
                })()}
                grandTotal={(() => {
                  // Use the same logic as EstimateTableV2
                  const calculatedGrandTotal = allProducts.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
                  return calculatedGrandTotal;
                })()}
                urdPurchaseData={oldSalesData}
                urdPurchaseAmount={oldItemsAmount}
                salesReturnData={saleReturnData}
                salesReturnAmount={salesAmountToPass}
                netPayableAmount={(() => {
                  // Use the same logic as EstimateTableV2
                  const netPayableAmountValue = parseFloat(netPayableAmount) || 0;
                  return netPayableAmountValue;
                })()}
                festivalDiscountAmt={festivalDiscountAmt}
              />
            }
          >
            {({ blob, loading, error }) => {

              
              if (shouldPrint && blob && !loading && !error) {
                const blobUrl = URL.createObjectURL(blob);
                const newWindow = window.open(blobUrl, "_blank");
                if (newWindow) {
                  newWindow.onload = () => {
                    newWindow.focus();
                    newWindow.print();
                  };
                  setTimeout(() => {
                    newWindow.focus();
                    newWindow.print();
                  }, 500);
                } else {
                  alert("Please allow pop-ups to view and print the PDF.");
                }
                // reset flag to avoid multiple prints
                setShouldPrint(false);
                // After printing, reload page to clear all entries
                if (shouldResetAfterPrint) {
                  setShouldResetAfterPrint(false);
                  try {
                    navigate('/estimates/v2', { replace: true });
                  } catch (e) {}
                  setTimeout(() => {
                    window.location.reload();
                  }, 250);
                }
              }
              return (
                <button
                  id="download-estimate-pdf"
                  className="btn btn-primary d-none"
                  disabled={loading || error || !blob}
                />
              );
            }}
          </BlobProvider>
        </div>
      </Container>
    </div>
  );
};

export default EstimateFormV2; 